import type {
  CalendarApi,
  Calendar as CalendarInstance,
  CalendarOptions,
  EventClickArg,
  EventDropArg,
  EventHoveringArg,
} from '@fullcalendar/core'
import { formatIsoTimeString, interactionSettingsStore } from '@fullcalendar/core/internal'
import enGbLocale from '@fullcalendar/core/locales/en-gb'
import type { DateClickArg } from '@fullcalendar/interaction'
import type FullCalendar from '@fullcalendar/react'
import { TimeCols } from '@fullcalendar/timegrid/internal'
import {
  createContext,
  type ReactNode,
  useContext,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'

import {
  type AppointmentStatus,
  type AppointmentTypeId,
  type AppointmentWithRelations,
  type BlockedSlotWithStaff,
  type ClinicId,
  getClinicRegion,
  getClinicTimezone,
  toWallClockDate,
} from '@snootsvet/shared'

import {
  CALENDAR_VIEWS,
  type CalendarViewType,
  getDefaultScrollTime,
} from '@/frontend/features/appointments/appointment-calendar.helpers'

export interface AppointmentCalendarState {
  clinicId: ClinicId
  view: CalendarViewType
  date: string
  staffIds: string[]
  statuses: AppointmentStatus[]
}

interface AppointmentCalendarProviderProps {
  value: AppointmentCalendarState
  onChange: (changes: Partial<AppointmentCalendarState>) => void
  initialTime?: string
  children: ReactNode
}

// Interaction state and transitions

type AvailabilityRequest =
  | { type: 'booking'; appointment: { appointmentTypeId: AppointmentTypeId } }
  | { type: 'rescheduling'; appointment: AppointmentWithRelations }

export type CalendarAvailability = AvailabilityRequest & {
  previousPosition: CalendarPosition & { time?: string }
}

type CreateParams = {
  date: Date
  endDate?: Date
  staffId?: string
  clinicId: ClinicId
  appointmentTypeId?: AppointmentTypeId
}

type EditParams = {
  clinicId?: ClinicId
  startTime: string
  endTime: string
  staffId?: string
}

export interface CalendarInteractionState {
  availability: CalendarAvailability | null
  modal:
    | { type: 'view'; appointment: AppointmentWithRelations }
    | { type: 'edit'; appointment: AppointmentWithRelations; params?: EditParams }
    | { type: 'create'; params: CreateParams }
    | { type: 'block-view'; block: BlockedSlotWithStaff }
    | { type: 'block-create'; params: CreateParams }
    | null
  hoveredEvent:
    | { type: 'appointment'; appointment: AppointmentWithRelations; el: HTMLElement }
    | { type: 'block'; block: BlockedSlotWithStaff; el: HTMLElement }
    | null
}

type CalendarAction =
  | { type: 'REQUEST_AVAILABILITY'; availability: CalendarAvailability }
  | { type: 'CLEAR_AVAILABILITY' }
  | { type: 'VIEW_APPOINTMENT'; appointment: AppointmentWithRelations }
  | { type: 'EDIT_APPOINTMENT'; appointment: AppointmentWithRelations; params?: EditParams }
  | { type: 'CREATE_APPOINTMENT'; params: CreateParams }
  | { type: 'VIEW_BLOCK'; block: BlockedSlotWithStaff }
  | { type: 'CREATE_BLOCK'; params: CreateParams }
  | { type: 'HOVER_APPOINTMENT'; appointment: AppointmentWithRelations; el: HTMLElement }
  | { type: 'HOVER_BLOCK'; block: BlockedSlotWithStaff; el: HTMLElement }
  | { type: 'CLEAR_HOVER' }
  | { type: 'DISMISS' }

function reducer(
  state: CalendarInteractionState,
  action: CalendarAction
): CalendarInteractionState {
  switch (action.type) {
    case 'REQUEST_AVAILABILITY':
      return {
        ...state,
        availability: action.availability,
        modal: action.availability.type === 'rescheduling' ? null : state.modal,
      }
    case 'CLEAR_AVAILABILITY':
      return { ...state, availability: null }
    case 'VIEW_APPOINTMENT':
      return { ...state, modal: { type: 'view', appointment: action.appointment } }
    case 'EDIT_APPOINTMENT':
      return {
        ...state,
        modal: { type: 'edit', appointment: action.appointment, params: action.params },
      }
    case 'CREATE_APPOINTMENT':
      return { ...state, modal: { type: 'create', params: action.params } }
    case 'VIEW_BLOCK':
      return { ...state, modal: { type: 'block-view', block: action.block } }
    case 'CREATE_BLOCK':
      return { ...state, modal: { type: 'block-create', params: action.params } }
    case 'HOVER_APPOINTMENT':
      return {
        ...state,
        hoveredEvent: { type: 'appointment', appointment: action.appointment, el: action.el },
      }
    case 'HOVER_BLOCK':
      return { ...state, hoveredEvent: { type: 'block', block: action.block, el: action.el } }
    case 'CLEAR_HOVER':
      return { ...state, hoveredEvent: null }
    case 'DISMISS':
      return { ...state, modal: null }
  }
}

// Shared context and provider

const AppointmentCalendarContext = createContext<{
  state: AppointmentCalendarState
  calendar: ReturnType<typeof useFullCalendarAdapter>
  availability: CalendarAvailability | null
  modal: CalendarInteractionState['modal']
  hoveredEvent: CalendarInteractionState['hoveredEvent']
  pendingChanges: Record<string, EditParams>
  eventHandlers: {
    eventClick: (info: EventClickArg) => void
    eventDrop: (info: EventDropArg) => void
    dateClick: (info: DateClickArg) => void
    eventMouseEnter: (info: EventHoveringArg) => void
    eventMouseLeave: () => void
    eventDragStart: () => void
  }
  requestAvailability: (request: AvailabilityRequest) => void
  cancelAvailabilityRequest: () => void
  dispatch: (action: CalendarAction) => void
  onAppointmentSaved: (appointment: AppointmentWithRelations) => void
  discardPendingChanges: () => void
} | null>(null)

export function AppointmentCalendarProvider({
  value: state,
  onChange,
  initialTime,
  children,
}: AppointmentCalendarProviderProps) {
  const calendar = useFullCalendarAdapter({
    position: { clinic: state.clinicId, view: state.view, date: state.date },
    onPositionChange: ({ clinic, view, date }) => onChange({ clinicId: clinic, view, date }),
    initialTime,
  })
  const { staffIds } = state
  const { navigateTo, capturePosition } = calendar
  const clinicId = calendar.calendarPosition.clinic

  const [{ availability, modal, hoveredEvent }, dispatch] = useReducer(reducer, {
    availability: null,
    modal: null,
    hoveredEvent: null,
  })
  const [pendingChanges, setPendingChanges] = useState<Record<string, EditParams>>({})
  const dragRevertRef = useRef<(() => void) | null>(null)

  // FullCalendar event handlers

  function handleEventClick(info: EventClickArg) {
    if (info.event.extendedProps.isAvailabilitySlot) {
      if (!clinicId) return
      const staffId = info.event.extendedProps.staffId as string | undefined
      if (availability?.type === 'rescheduling') {
        dispatch({
          type: 'EDIT_APPOINTMENT',
          appointment: availability.appointment,
          params: {
            clinicId,
            startTime: info.event.start!.toISOString(),
            endTime: info.event.end!.toISOString(),
            staffId,
          },
        })
      } else {
        dispatch({
          type: 'CREATE_APPOINTMENT',
          params: {
            date: info.event.start!,
            endDate: info.event.end ?? undefined,
            appointmentTypeId: info.event.extendedProps.appointmentTypeId as AppointmentTypeId,
            staffId,
            clinicId,
          },
        })
      }
      return
    }
    // Booking and rescheduling searches restrict event clicks to availability slots.
    if (availability) return

    if (info.event.display === 'background') return
    if (info.event.extendedProps.type === 'block') {
      dispatch({
        type: 'VIEW_BLOCK',
        block: info.event.extendedProps.block as BlockedSlotWithStaff,
      })
    } else {
      dispatch({
        type: 'VIEW_APPOINTMENT',
        appointment: info.event.extendedProps.appointment as AppointmentWithRelations,
      })
    }
  }

  function handleEventDrop(info: EventDropArg) {
    const appointment = info.event.extendedProps.appointment as AppointmentWithRelations
    if (!appointment) return
    const newStart = info.event.start!
    const durationMs =
      new Date(appointment.endTime).getTime() - new Date(appointment.startTime).getTime()
    const newEnd = new Date(newStart.getTime() + durationMs)
    const newResourceId = (info as EventDropArg & { newResource?: { id: string } }).newResource?.id
    const params: EditParams = {
      startTime: newStart.toISOString(),
      endTime: newEnd.toISOString(),
      ...(newResourceId ? { staffId: newResourceId } : {}),
    }
    dragRevertRef.current = info.revert
    setPendingChanges((previous) => ({ ...previous, [appointment.id]: params }))
    dispatch({ type: 'EDIT_APPOINTMENT', appointment, params })
  }

  function handleDateClick(info: DateClickArg) {
    if (availability || !clinicId) return
    const resourceStaffId = (info as DateClickArg & { resource?: { id: string } }).resource?.id
    const staffId = resourceStaffId ?? (staffIds.length === 1 ? staffIds[0] : undefined)
    dispatch({
      type: info.jsEvent.altKey ? 'CREATE_BLOCK' : 'CREATE_APPOINTMENT',
      params: { date: info.date, staffId, clinicId },
    })
  }

  function handleEventMouseEnter(info: EventHoveringArg) {
    if (info.event.display === 'background' || info.event.extendedProps.isAvailabilitySlot) return
    if (info.event.extendedProps.type === 'block') {
      dispatch({
        type: 'HOVER_BLOCK',
        block: info.event.extendedProps.block as BlockedSlotWithStaff,
        el: info.el,
      })
    } else {
      dispatch({
        type: 'HOVER_APPOINTMENT',
        appointment: info.event.extendedProps.appointment as AppointmentWithRelations,
        el: info.el,
      })
    }
  }

  // Workflow operations

  /** Captures FullCalendar's live viewport for the return position and coordinates the search transition with navigation to week view. */
  function requestAvailability(request: AvailabilityRequest) {
    const previousPosition = capturePosition()
    if (!previousPosition) return
    dispatch({
      type: 'REQUEST_AVAILABILITY',
      availability: { ...request, previousPosition },
    })
    const { clinic, date } = previousPosition
    navigateTo({ clinic, date, view: 'week' })
  }

  /** Restores the captured FullCalendar position and scroll time alongside clearing the search state. */
  function cancelAvailabilityRequest() {
    if (!availability) return
    navigateTo(availability.previousPosition)
    dispatch({ type: 'CLEAR_AVAILABILITY' })
  }

  /** Coordinates the persisted appointment's detail view and search completion with drag-preview cleanup, notification, and return navigation. */
  function onAppointmentSaved(appointment: AppointmentWithRelations) {
    const rescheduled =
      availability?.type === 'rescheduling' ||
      (modal?.type === 'edit' && modal.appointment.startTime !== appointment.startTime)
    toast.success(
      `Successfully ${modal?.type === 'create' ? 'booked' : rescheduled ? 'rescheduled' : 'updated'} ${appointment.appointmentTypeName} appointment for ${appointment.patient.name}`
    )
    dragRevertRef.current = null
    setPendingChanges({})
    dispatch({ type: 'VIEW_APPOINTMENT', appointment })
    dispatch({ type: 'CLEAR_AVAILABILITY' })
    if (availability?.previousPosition.view === 'day' && appointment.clinicId) {
      const time = capturePosition()?.time
      navigateTo({
        clinic: appointment.clinicId,
        view: 'day',
        date: toWallClockDate(
          new Date(appointment.startTime),
          getClinicTimezone(appointment.clinicId)
        ),
        time,
      })
    }
  }

  /** Reverts FullCalendar's provisional drag and clears its React preview together so both representations return to the saved appointment. */
  function discardPendingChanges() {
    dragRevertRef.current?.()
    dragRevertRef.current = null
    setPendingChanges({})
  }

  return (
    <AppointmentCalendarContext
      value={{
        state,
        calendar,
        availability,
        modal,
        hoveredEvent,
        pendingChanges,
        eventHandlers: {
          eventClick: handleEventClick,
          eventDrop: handleEventDrop,
          dateClick: handleDateClick,
          eventMouseEnter: handleEventMouseEnter,
          eventMouseLeave: () => dispatch({ type: 'CLEAR_HOVER' }),
          eventDragStart: () => dispatch({ type: 'CLEAR_HOVER' }),
        },
        requestAvailability,
        cancelAvailabilityRequest,
        dispatch,
        onAppointmentSaved,
        discardPendingChanges,
      }}
    >
      {children}
    </AppointmentCalendarContext>
  )
}

export function useAppointmentCalendarContext() {
  const context = useContext(AppointmentCalendarContext)
  if (!context) throw new Error('AppointmentCalendarProvider is required')
  return context
}

// FullCalendar adapter

/** The displayed clinic, view, and clinic-local anchor date, synchronized with URL filters. */
interface CalendarPosition {
  clinic: ClinicId
  view: CalendarViewType
  date: string
}

/** A navigation request that changes selected parts of the position and optionally scrolls to a clinic-local time. */
type CalendarNavigation = Partial<CalendarPosition> & { time?: string }

interface UseFullCalendarAdapterOpts {
  position: CalendarPosition
  onPositionChange: (position: CalendarPosition) => void
  initialTime?: string
}

/**
 * Synchronizes React position and FullCalendar in both directions.
 * Prop changes reconcile the calendar before paint; internal navigation publishes its final position.
 * Clinic timezone, view, date, and scrolling are applied as one synchronous navigation operation.
 * View changes preserve the visible time unless navigation specifies a time target.
 * React receives the calendar's actual position and visible range together for rendering and data fetching.
 */
function useFullCalendarAdapter({
  position,
  onPositionChange,
  initialTime,
}: UseFullCalendarAdapterOpts) {
  const calendarRef = useRef<FullCalendar>(null)
  const [initialPosition] = useState(position)
  const pendingInitialTime = useRef<string | undefined>(
    initialTime ?? getDefaultScrollTime(getClinicTimezone(position.clinic))
  )
  const initialClinicOptions = useMemo(
    () => getClinicCalendarOptions(initialPosition.clinic),
    [initialPosition]
  )

  // FullCalendar knows timezones; clinic identity is tracked separately because clinics can share one.
  const activeClinic = useRef(position.clinic)
  const lastAppliedPosition = useRef(position)
  const applyingNavigation = useRef(false)
  const [rendered, setRendered] = useState<{
    position: CalendarPosition
    dateRange: { from: Date; to: Date }
  } | null>(null)

  /** Applies timezone and regional display options before interpreting destination dates. */
  function applyClinicTimezone(calendar: CalendarInstance, clinic: ClinicId) {
    activeClinic.current = clinic
    if (calendar.getOption('timeZone') === getClinicTimezone(clinic)) return
    const options = getClinicCalendarOptions(clinic)
    calendar.setOption('timeZone', options.timeZone)
    calendar.setOption('locale', options.locale)
    calendar.setOption('slotLabelFormat', options.slotLabelFormat)
  }

  function readPosition(calendar: CalendarApi): CalendarPosition {
    const view = Object.entries(CALENDAR_VIEWS).find(
      ([, value]) => value === calendar.view.type
    )?.[0] as CalendarViewType | undefined
    if (!view) throw new Error(`Received unsupported view type ${calendar.view.type}`)
    const clinic = activeClinic.current
    return {
      clinic,
      view,
      date: toWallClockDate(calendar.getDate(), getClinicTimezone(clinic)),
    }
  }

  /** Captures the current position and top visible time for later navigation. */
  function capturePosition() {
    const calendar = calendarRef.current?.getApi()
    if (!calendar) return null
    return { ...readPosition(calendar), time: getCalendarScrollTime(calendar) }
  }

  /** Reads the settled calendar position and publishes changed values to React. */
  function publishCalendarSnapshot() {
    const calendar = calendarRef.current?.getApi()
    if (!calendar) return
    const nextPosition = readPosition(calendar)
    const dateRange = { from: calendar.view.activeStart, to: calendar.view.activeEnd }
    // Position and range describe the same rendered calendar, including its active timezone.
    setRendered((previous) =>
      previous &&
      sameCalendarPosition(previous.position, nextPosition) &&
      previous.dateRange.from.getTime() === dateRange.from.getTime() &&
      previous.dateRange.to.getTime() === dateRange.to.getTime()
        ? previous
        : { position: nextPosition, dateRange }
    )
    if (!sameCalendarPosition(position, nextPosition)) onPositionChange(nextPosition)
  }

  function navigateTo(destination: CalendarNavigation) {
    const calendar = calendarRef.current?.getApi() as CalendarInstance | undefined
    if (!calendar) return false
    const clinic = destination.clinic ?? activeClinic.current
    const viewChanged =
      !!destination.view && calendar.view.type !== CALENDAR_VIEWS[destination.view]
    const time = destination.time ?? (viewChanged ? getCalendarScrollTime(calendar) : undefined)
    // Preserve the visible calendar day across timezone changes when a destination date is omitted.
    const date =
      destination.date ??
      toWallClockDate(calendar.getDate(), getClinicTimezone(activeClinic.current))
    applyingNavigation.current = true
    try {
      calendar.batchRendering(() => {
        applyClinicTimezone(calendar, clinic)
        const dateChanged = toWallClockDate(calendar.getDate(), getClinicTimezone(clinic)) !== date
        if (destination.view && calendar.view.type !== CALENDAR_VIEWS[destination.view]) {
          calendar.changeView(CALENDAR_VIEWS[destination.view], dateChanged ? date : undefined)
        } else if (dateChanged) {
          calendar.gotoDate(date)
        }
      })
      if (time) calendar.scrollToTime(time)
    } finally {
      applyingNavigation.current = false
    }
    publishCalendarSnapshot()
    return true
  }

  /** Reconciles the requested position and applies the initial scroll target once. */
  const applyRequestedPosition = useEffectEvent((nextPosition: CalendarPosition) => {
    if (!navigateTo({ ...nextPosition, time: pendingInitialTime.current })) return
    pendingInitialTime.current = undefined
    lastAppliedPosition.current = nextPosition
  })
  const { clinic, view, date } = position
  useLayoutEffect(() => {
    applyRequestedPosition({ clinic, view, date })
  }, [clinic, view, date])

  function handleDatesSet() {
    // Batched navigation can emit intermediate dates. Prop updates also await reconciliation in the layout effect.
    if (applyingNavigation.current || !sameCalendarPosition(lastAppliedPosition.current, position))
      return
    publishCalendarSnapshot()
  }

  return {
    calendarRef,
    initialPosition,
    initialClinicOptions,
    calendarPosition: rendered?.position ?? initialPosition,
    dateRange: rendered?.dateRange ?? null,
    navigateTo,
    capturePosition,
    handleDatesSet,
  }
}

/** FullCalendar display options for a clinic's timezone and regional time format. */
function getClinicCalendarOptions(clinic: ClinicId) {
  const timeZone = getClinicTimezone(clinic)
  const { hour12 } = getClinicRegion(clinic)
  return {
    timeZone,
    locale: timeZone === 'Europe/London' ? enGbLocale : 'en',
    slotLabelFormat: { hour: 'numeric', minute: '2-digit', hour12 },
  } satisfies Pick<CalendarOptions, 'timeZone' | 'locale' | 'slotLabelFormat'>
}

/**
 * Reads the top visible time using FullCalendar v6's private time-grid hit testing.
 * Hit testing uses the grid's slot geometry and snap duration, including the active zoom level.
 */
function getCalendarScrollTime(calendar: CalendarApi): string | undefined {
  const grid = Object.values(interactionSettingsStore).find(
    ({ component }) => component instanceof TimeCols && component.context.calendarApi === calendar
  )
  if (!grid || !(grid.component instanceof TimeCols) || !grid.component.state.slatCoords) return
  const scroller = grid.el.closest<HTMLElement>('.fc-scroller')
  if (!scroller?.clientHeight) return
  const bodyRect = grid.el.getBoundingClientRect()
  const scrollRect = scroller.getBoundingClientRect()
  const hit = grid.component.queryHit(
    scrollRect.left + scroller.clientWidth / 2 - bodyRect.left,
    Math.max(0, scrollRect.top + scroller.clientTop - bodyRect.top) + 1
  )
  const start = hit?.dateSpan.range.start
  return start ? formatIsoTimeString(start) : undefined
}

function sameCalendarPosition(a: CalendarPosition, b: CalendarPosition) {
  return a.clinic === b.clinic && a.view === b.view && a.date === b.date
}
